import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { trackTelegramClick } from "../utils/telegramTracking";

/**
 * Unified promo dock — replaces the three separate floating bubbles
 * (Klarna, welcome 10%, EU customs) with one tidy stack of small pills
 * anchored bottom-left. Clicking a pill opens ONE compact card above the
 * dock, so promos never cover page content.
 */
const WELCOME_KEY = "welcomeBubbleDismissedAt";
const CUSTOMS_KEY = "euCustomsBubbleDismissedAt";
const TELEGRAM_KEY = "telegramBubbleDismissedAt";
const AUTO_KEY = "promoDockAutoExpandedAt";
const TELEGRAM_URL = "https://t.me/edicolavillaricca";
const DAYS7 = 7 * 24 * 3600 * 1000;

const recentlyDismissed = (key) => {
  try {
    return Date.now() - parseInt(localStorage.getItem(key) || "0", 10) < DAYS7;
  } catch (e) {
    return false;
  }
};

export const PromoDock = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(null); // "klarna" | "welcome" | "customs" | null
  const [customsDetails, setCustomsDetails] = useState(false);

  const hideOnPaths = ["/gestione", "/checkout", "/admin", "/login", "/paga", "/p/", "/accedi", "/registrazione"];
  const shouldHide = hideOnPaths.some((p) => pathname.startsWith(p));
  const isHome = pathname === "/" || pathname === "/home";

  let isLoggedIn = false;
  try {
    isLoggedIn = !!(sessionStorage.getItem("access_token") || localStorage.getItem("access_token"));
  } catch (e) { /* ignore */ }

  const showWelcome = !isLoggedIn;
  const showCustoms = isHome;
  const showTelegram = isHome && !recentlyDismissed(TELEGRAM_KEY);

  // Auto-expand the welcome promo once every 7 days (non-invasive: single card, closable)
  useEffect(() => {
    if (shouldHide || !showWelcome) return;
    if (recentlyDismissed(WELCOME_KEY) || recentlyDismissed(AUTO_KEY)) return;
    const t = setTimeout(() => {
      setOpen("welcome");
      try { localStorage.setItem(AUTO_KEY, String(Date.now())); } catch (e) { /* ignore */ }
    }, 1800);
    return () => clearTimeout(t);
  }, [shouldHide, showWelcome]);

  if (shouldHide) return null;

  const closeCard = (persistKey) => {
    if (persistKey) {
      try { localStorage.setItem(persistKey, String(Date.now())); } catch (e) { /* ignore */ }
    }
    setOpen(null);
  };

  const pill = (id, label, cls, testid) => (
    <button
      key={id}
      type="button"
      onClick={() => setOpen(open === id ? null : id)}
      data-testid={testid}
      className={`flex items-center gap-1.5 rounded-full pl-2.5 pr-3 py-1.5 text-[11px] font-extrabold shadow-lg border transition-transform hover:scale-105 ${cls} ${open === id ? "ring-2 ring-white/60" : ""}`}
    >
      {label}
    </button>
  );

  return (
    <div className="fixed left-3 bottom-3 z-[55] flex flex-col items-start gap-2 max-w-[290px]" data-testid="promo-dock">
      {/* ── Expanded card (one at a time) ── */}
      {open === "klarna" && (
        <div className="relative bg-gradient-to-br from-[#FFA8C5] to-[#FF87B2] rounded-2xl px-4 py-3 text-black border-2 border-white shadow-2xl" data-testid="klarna-promo-bubble">
          <button onClick={() => setOpen(null)} aria-label="Chiudi promo Klarna" data-testid="klarna-promo-collapse"
            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-black text-white text-xs leading-none flex items-center justify-center hover:bg-gray-800">×</button>
          <Link to="/prodotti" className="block" onClick={() => setOpen(null)}>
            <div className="flex items-center gap-2 mb-1">
              <span className="bg-black text-[#FFA8C5] font-extrabold text-xs px-2 py-0.5 rounded-full leading-none">Klarna.</span>
              <span className="text-[10px] uppercase tracking-wider font-bold">Novità</span>
            </div>
            <p className="text-sm font-extrabold leading-tight">Compra ora, paga in 3 rate</p>
            <p className="text-[11px] mt-1 leading-snug">Fino a <strong>€3.000</strong> · <strong>0% interessi</strong></p>
            <div className="mt-2 inline-block bg-black text-white text-[11px] font-bold px-3 py-1 rounded-full">Scopri i prodotti →</div>
          </Link>
        </div>
      )}

      {open === "welcome" && showWelcome && (
        <div className="relative bg-gradient-to-br from-amber-400 via-amber-500 to-orange-500 text-white rounded-2xl px-4 py-3.5 shadow-2xl" data-testid="welcome-discount-bubble">
          <button onClick={() => closeCard(WELCOME_KEY)} aria-label="Chiudi" data-testid="welcome-discount-dismiss"
            className="absolute -top-2 -right-2 w-6 h-6 bg-white text-amber-700 rounded-full text-xs font-bold shadow-md hover:bg-gray-100 flex items-center justify-center">×</button>
          <Link to="/registrazione" className="block" onClick={() => setOpen(null)}>
            <div className="flex items-start gap-2">
              <span className="text-2xl leading-none">🎁</span>
              <div>
                <div className="text-sm font-bold leading-tight mb-1">
                  Iscriviti e ricevi il <span className="bg-white text-amber-700 px-1.5 py-0.5 rounded font-extrabold">10% di sconto</span>
                </div>
                <div className="text-[11px] text-white/90 leading-snug">Solo per nuovi iscritti. Coupon valido 60 giorni sul primo ordine.</div>
                <div className="mt-2 inline-block bg-white/90 text-amber-700 text-[11px] font-bold px-3 py-1 rounded-full">👉 Iscriviti ora</div>
              </div>
            </div>
          </Link>
        </div>
      )}

      {open === "customs" && showCustoms && (
        <div className="relative bg-gradient-to-br from-blue-700 via-blue-800 to-indigo-900 text-white rounded-2xl px-4 py-3.5 shadow-2xl border border-blue-400/30" data-testid="eu-customs-bubble">
          <button onClick={() => closeCard(CUSTOMS_KEY)} aria-label="Chiudi avviso dogana" data-testid="eu-customs-dismiss"
            className="absolute -top-2 -right-2 w-6 h-6 bg-white text-blue-800 rounded-full text-xs font-bold shadow-md hover:bg-gray-100 flex items-center justify-center">×</button>
          <div className="flex items-start gap-2.5">
            <span className="text-2xl leading-none mt-0.5">📦</span>
            <div className="min-w-0">
              <div className="text-sm font-bold leading-tight mb-1">
                Novità dogana UE <span className="text-blue-200 font-semibold">— dal 1° luglio 2026</span>
              </div>
              <div className="text-xs text-blue-100 leading-snug">
                Spediamo dall'Italia 🇮🇹: <strong className="text-white">nessun dazio per i clienti in UE</strong>.
              </div>
              {customsDetails && (
                <div className="mt-2 space-y-1.5 text-[11px] text-blue-100 leading-snug border-t border-blue-400/30 pt-2" data-testid="eu-customs-details">
                  <p><strong className="text-white">Cosa cambia:</strong> l'UE elimina l'esenzione doganale per le importazioni sotto i 150&nbsp;€ da paesi extra-UE. Si applicherà un dazio forfettario di <strong className="text-white">3&nbsp;€ per tipologia di articolo</strong>.</p>
                  <p><strong className="text-white">Ordini fuori UE</strong> (es. UK, USA, Svizzera): eventuali dazi e tasse locali alla consegna restano a carico del destinatario, secondo le regole del paese di destinazione.</p>
                  <p><strong className="text-white">Resi verso l'Italia da paesi extra-UE:</strong> dal 1/7/2026 saranno soggetti al dazio UE di 3&nbsp;€ per articolo, <strong className="text-white">non rimborsabile</strong>.</p>
                </div>
              )}
              <button onClick={() => setCustomsDetails((v) => !v)} data-testid="eu-customs-toggle-details"
                className="mt-2 inline-block bg-white/90 text-blue-800 text-[11px] font-bold px-3 py-1 rounded-full hover:bg-white transition-colors">
                {customsDetails ? "Nascondi dettagli ▲" : "Leggi i dettagli ▼"}
              </button>
            </div>
          </div>
        </div>
      )}

      {open === "telegram" && showTelegram && (
        <div className="relative bg-gradient-to-br from-[#229ED9] via-[#1b8ac4] to-[#0f5f8c] text-white rounded-2xl px-4 py-3.5 shadow-2xl border border-sky-300/40" data-testid="telegram-promo-bubble">
          <button onClick={() => closeCard(TELEGRAM_KEY)} aria-label="Chiudi invito Telegram" data-testid="telegram-promo-dismiss"
            className="absolute -top-2 -right-2 w-6 h-6 bg-white text-[#1b8ac4] rounded-full text-xs font-bold shadow-md hover:bg-gray-100 flex items-center justify-center">×</button>
          <div className="flex items-start gap-2.5">
            <span className="text-2xl leading-none mt-0.5">📣</span>
            <div className="min-w-0">
              <div className="text-sm font-bold leading-tight mb-1">Seguici su Telegram</div>
              <div className="text-xs text-sky-100 leading-snug">
                Nuovi arrivi in anteprima, offerte lampo e un <strong className="text-white">codice sconto esclusivo</strong> solo per gli iscritti al canale.
              </div>
              <a href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid="telegram-promo-link"
                onClick={() => { trackTelegramClick("home_popup"); closeCard(TELEGRAM_KEY); }}
                className="mt-2 inline-block bg-white text-[#1b8ac4] text-[11px] font-extrabold px-3 py-1 rounded-full hover:bg-sky-50 transition-colors">
                Iscriviti al canale →
              </a>
            </div>
          </div>
        </div>
      )}

      {/* ── Pills (always compact, stacked) ── */}
      <div className="flex flex-wrap items-center gap-2">
        {pill("klarna", (
          <>
            <span className="bg-black text-[#FFA8C5] font-extrabold text-[10px] px-1.5 py-0.5 rounded-full leading-none">Klarna.</span>
            <span>3 rate</span>
          </>
        ), "bg-gradient-to-br from-[#FFA8C5] to-[#FF87B2] text-black border-white", "klarna-promo-mini")}
        {showWelcome && pill("welcome", <><span>🎁</span><span>-10%</span></>, "bg-gradient-to-br from-amber-400 to-orange-500 text-white border-white/70", "welcome-discount-mini")}
        {showCustoms && pill("customs", <><span>📦</span><span>Dogana UE</span></>, "bg-gradient-to-br from-blue-700 to-indigo-900 text-white border-blue-300/50", "eu-customs-mini")}
        {showTelegram && pill("telegram", <><span>📣</span><span>Telegram</span></>, "bg-gradient-to-br from-[#229ED9] to-[#0f5f8c] text-white border-sky-300/60", "telegram-promo-mini")}
      </div>
    </div>
  );
};

export default PromoDock;
